"use client";

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@/components/theme-provider";
import { Card } from "@/components/ui/card";

function chartColors() {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();
  return {
    text: v("--text-muted"),
    grid: v("--border"),
    accent: v("--accent"),
    gold: v("--gold"),
    success: v("--success"),
    danger: v("--danger"),
    warning: v("--warning"),
    card: v("--bg-card"),
    border: v("--border-strong"),
  };
}

export function DashboardCharts({
  timeline,
  casesByStatus,
  reportsByProvider,
  similarity,
  actionedVsRejected,
}: {
  timeline: { date: string; count: number }[];
  casesByStatus: { name: string; value: number }[];
  reportsByProvider: { name: string; value: number }[];
  similarity: { name: string; value: number }[];
  actionedVsRejected: { name: string; value: number }[];
}) {
  const { theme } = useTheme();
  const c = typeof document === "undefined" ? null : chartColors();
  const colors = c
    ? [c.accent, c.success, c.gold, c.warning, c.danger, c.text]
    : ["#6e2f1f", "#2f6a45", "#8a6a22", "#9a5b12", "#9a2d2d", "#5c5347"];
  const axis = c?.text ?? "#5c5347";
  const grid = c?.grid ?? "#c9b89a";
  const tooltipStyle = {
    background: c?.card ?? "#fffaf1",
    border: `1px solid ${c?.border ?? "#8d7754"}`,
    borderRadius: 4,
    color: c?.text ?? "#1f1a14",
  };

  return (
    <div key={theme} className="grid gap-4 xl:grid-cols-2">
      <ChartCard title="When copies were found">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={timeline}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="date" stroke={axis} fontSize={11} />
            <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="count" stroke={colors[0]} fill={colors[0]} fillOpacity={0.18} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="Cases by status">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={casesByStatus}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="name" stroke={axis} fontSize={11} />
            <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="value" fill={colors[1]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="Reports by provider">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={reportsByProvider} layout="vertical">
            <CartesianGrid stroke={grid} horizontal={false} />
            <XAxis type="number" stroke={axis} fontSize={11} allowDecimals={false} />
            <YAxis type="category" dataKey="name" stroke={axis} fontSize={11} width={120} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="value" fill={colors[2]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="How similar">
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={similarity} dataKey="value" nameKey="name" innerRadius={52} outerRadius={84}>
              {similarity.map((entry, i) => (
                <Cell key={entry.name} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="Actioned vs rejected">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={actionedVsRejected}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis dataKey="name" stroke={axis} fontSize={11} />
            <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="value" fill={colors[0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-5">
      <p className="serif mb-3">{title}</p>
      {children}
    </Card>
  );
}
